/**
 * TanStack Query client + SWR compatibility adapter.
 *
 * Provides `useQuery` wrappers that mimic useSWR API surface
 * to minimize code changes during migration.
 */
import { useMemo, useCallback } from "react";
import {
  useQuery as useTanstackQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { errorHandlingFetcher, FetchError } from "@/shared/lib/fetcher";
import type { QueryKey } from "@tanstack/react-query";

// Skips retries for 4xx responses (auth errors, 429 rate limits, etc.) —
// mirrors the smart retry used by App.tsx's global QueryClient default.
// Without this, every useSWRCompat call falls back to a dumb numeric retry
// count that retries blindly even on 429, adding load to an already
// rate-limited backend.
function defaultSwrRetry(failureCount: number, error: unknown): boolean {
  if (
    error instanceof FetchError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
}

export { useMutation, useQueryClient };
export { useQuery } from "@tanstack/react-query";

interface SWRLikeOptions<T> {
  refreshInterval?: number;
  enabled?: boolean;
  keepPreviousData?: boolean;
  retry?: boolean | number;
  onError?: (err: Error) => void;
  revalidateIfStale?: boolean;
  dedupingInterval?: number;
  shouldRetryOnError?: boolean;
}

/**
 * useSWRCompat — drop-in replacement for useSWR.
 * Returns { data, error, isLoading, mutate } matching SWR API.
 */
export function useSWRCompat<T>(
  key: string | null | false | undefined | QueryKey,
  fetcher: (url: string) => Promise<T> = errorHandlingFetcher,
  options: SWRLikeOptions<T> = {}
): {
  data: T | undefined;
  error: Error | undefined;
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => void;
} {
  const queryClient = useQueryClient();

  // Ổn định queryKey theo giá trị thật của `key`, không theo identity của
  // array literal — tránh tạo mảng mới mỗi render (nguồn gốc BUG-API-UI5-021,
  // xem thêm ui/specs/bugs/ui-v4/BUG-UI-V4-003 cho vòng lặp vô hạn ban đầu
  // trong ProjectsContext.tsx do vấn đề này gây ra).
  const queryKey = useMemo<QueryKey>(
    () => (Array.isArray(key) ? key : [key]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(key) ? JSON.stringify(key) : key]
  );

  const enabled =
    key !== null &&
    key !== false &&
    key !== undefined &&
    options.enabled !== false;

  const result = useTanstackQuery<T>({
    queryKey,
    queryFn: () => {
      const url = typeof key === "string" ? key : String(queryKey[0]);
      return fetcher(url);
    },
    enabled,
    refetchInterval: options.refreshInterval,
    retry:
      options.shouldRetryOnError === false || options.retry === false
        ? false
        : typeof options.retry === "number"
          ? options.retry
          : defaultSwrRetry,
  });

  // Ổn định `mutate` — trước đây là arrow function inline mới mỗi render,
  // phá vỡ mọi useCallback/useEffect ở nơi gọi có `mutate`/`refreshX` trong
  // dependency array (BUG-API-UI5-021: gây vòng lặp request vô hạn trong
  // ProjectsContext.tsx).
  const mutate = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey]
  );

  return {
    data: result.data,
    error: result.error as Error | undefined,
    isLoading: result.isLoading,
    isValidating: result.isFetching,
    mutate,
  };
}

/**
 * SWR-compatible `KeyedMutator`. `useSWRCompat().mutate` (a no-arg
 * "invalidate" function) is assignable to it; use `useKeyedMutator` when the
 * caller needs real SWR semantics (optimistic data + `{ revalidate: false }`).
 */
export type KeyedMutator<T> = (
  data?:
    | T
    | Promise<T | undefined>
    | ((current?: T) => T | undefined | Promise<T | undefined>),
  opts?: boolean | { revalidate?: boolean }
) => Promise<T | undefined | void> | void;

/**
 * Builds a `KeyedMutator` for a `useSWRCompat` string key on top of the
 * TanStack cache: writes optimistic data with `setQueryData`, then
 * invalidates unless `revalidate` is explicitly false.
 */
export function useKeyedMutator<T>(key: string): KeyedMutator<T> {
  const queryClient = useQueryClient();
  return useCallback<KeyedMutator<T>>(
    async (data, opts) => {
      let next: T | undefined;
      if (typeof data === "function") {
        const updater = data as (
          current?: T
        ) => T | undefined | Promise<T | undefined>;
        next = await updater(queryClient.getQueryData<T>([key]));
      } else if (data !== undefined) {
        next = await data;
      }
      if (next !== undefined) queryClient.setQueryData<T>([key], next);
      const revalidate =
        typeof opts === "boolean" ? opts : opts?.revalidate !== false;
      if (data === undefined || revalidate) {
        await queryClient.invalidateQueries({ queryKey: [key] });
      }
      return next;
    },
    [queryClient, key]
  );
}
